import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Mail, MessageCircle, BookOpen, ArrowRight, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const Support = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleTelegramClick = () => {
    window.open("https://t.me/voice2doc_support", "_blank");
  };

  const handleEmailClick = () => {
    window.location.href = "mailto:support@voice2doc.com";
  };

  const handleUserGuideClick = () => {
    navigate("/guide");
  };

  return (
    <MainLayout>
      <div className="container-page max-w-4xl py-8 sm:py-12">
        {/* Header Section */}
        <div className="text-center mb-12 space-y-4">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground">
            {t('support.title')}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {t('support.subtitle')}
          </p>
        </div>

        <div className="space-y-8">
          {/* User Guide - Hero Card */}
          <Card
            className="group relative overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer border-primary/10 bg-card hover:bg-accent/5"
            onClick={handleUserGuideClick}
          >
            <div className="absolute top-0 right-0 p-6 opacity-10 group-hover:opacity-20 transition-opacity">
              <BookOpen className="w-24 h-24 text-primary" />
            </div>
            <CardHeader className="relative z-10">
              <div className="flex items-start sm:items-center justify-between gap-4">
                <div className="flex items-start sm:items-center gap-4 sm:gap-6">
                  <div className="p-3 sm:p-4 bg-primary/10 rounded-xl text-primary shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <BookOpen className="h-6 w-6 sm:h-8 sm:w-8" />
                  </div>
                  <div className="space-y-1">
                    <CardTitle className="text-xl sm:text-2xl">{t('support.guide_title')}</CardTitle>
                    <CardDescription className="text-base">
                      {t('support.guide_desc')}
                    </CardDescription>
                  </div>
                </div>
                <div className="hidden sm:flex bg-background rounded-full p-2 shadow-sm border group-hover:translate-x-1 transition-transform">
                  <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Contact Options Grid */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Telegram */}
            <Card
              className="group hover:shadow-lg transition-all duration-300 cursor-pointer hover:-translate-y-1 border-blue-500/20 hover:border-blue-500/50"
              onClick={handleTelegramClick}
            >
              <CardHeader>
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-blue-500/10 rounded-xl text-blue-600 dark:text-blue-400 group-hover:bg-blue-500/20 transition-colors">
                    <MessageCircle className="h-6 w-6" />
                  </div>
                  <ExternalLink className="h-5 w-5 text-muted-foreground/50 group-hover:text-blue-500 transition-colors" />
                </div>
                <CardTitle className="text-xl mb-2">{t('support.telegram_title')}</CardTitle>
                <CardDescription>
                  {t('support.telegram_desc')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <span className="text-sm font-medium text-blue-600 dark:text-blue-400 group-hover:underline">
                  @voice2doc_support
                </span>
              </CardContent>
            </Card>

            {/* Email */}
            <Card
              className="group hover:shadow-lg transition-all duration-300 cursor-pointer hover:-translate-y-1 border-emerald-500/20 hover:border-emerald-500/50"
              onClick={handleEmailClick}
            >
              <CardHeader>
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-emerald-500/10 rounded-xl text-emerald-600 dark:text-emerald-400 group-hover:bg-emerald-500/20 transition-colors">
                    <Mail className="h-6 w-6" />
                  </div>
                  <ArrowRight className="h-5 w-5 text-muted-foreground/50 group-hover:text-emerald-500 transition-colors" />
                </div>
                <CardTitle className="text-xl mb-2">{t('support.email_title')}</CardTitle>
                <CardDescription>
                  {t('support.email_desc')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400 group-hover:underline">
                  support@voice2doc.com
                </span>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-16 text-center">
          <p className="text-sm text-muted-foreground">
            {t('support.footer_text')}
          </p>
        </div>
      </div>
    </MainLayout>
  );
};

export default Support;